import { createRouter, createWebHistory } from "vue-router";
import { useStore } from "vuex";
import sessionTimeout from "../utils/session-timeout";
import Balance from "../views/Balance/Balance.vue";
import Signup from "../views/auth/Signup.vue";
import SetPassword from "../views/auth/SetPassword.vue";
import Signin from "../views/auth/Signin.vue";
import Otp from "../views/auth/Otp.vue";
import VerifyEmail from "../views/auth/VerifyEmail.vue";
import VerifyCompleted from "../views/auth/VerifyCompleted.vue";
import DashboardApp from "../views/dashboard/Dashboard.vue";
import ForgotPassword from "../views/auth/ForgotPassword.vue";
import ForgotPasswordNotify from "../views/auth/ForgotPasswordNotify.vue";
import ResetPassword from "../views/auth/ResetPassword.vue";
import ResetPasswordNotify from "../views/auth/ResetPasswordNotify.vue";
import DetailCompany from "../views/company/DetailCompany.vue";
import Transaction from "../views/Transaction/Transaction.vue";
import TransactionDetail from "../views/Transaction/TransactionDetail.vue";
import DocumentCompany from "../views/company/DocumentCompany.vue";
import BankCompany from "../views/company/BankCompany.vue";
import HelpCenter from "../views/help/HelpCenter.vue";
import Transfer from "../views/Transfer/Transfer.vue";
import SingleTransfer from "../views/Transfer/SingleTransfer.vue";
import UploadTopup from "../views/UploadPPOB/UploadPPOB.vue";
import Profile from "../views/profile/Profile.vue";
import OtpVerification from "../views/auth/OtpVerification.vue";
import VirtualAccountDisplay from "../views/Transfer/components/VirtualAccountDisplay.vue";
import PaymentSuccess from "../views/Transfer/components/PaymentSuccess.vue"; // Import the new component
import SingleTransferPaymentSuccess from "../views/Transfer/components/SingleTransferPaymentSuccess.vue";
import PaymentCancelled from "../views/Transfer/components/PaymentCancelled.vue"; // Tambahkan import komponen baru
import PPOBPaymentSuccess from "../views/UploadPPOB/components/PaymentSuccess.vue"; // Import PPOB Payment Success
import PPOBVirtualAccountDisplay from "../views/UploadPPOB/components/VirtualAccountDisplay.vue"; // Import PPOB VA Display
import Integrations from "../views/Integrations/Integrations.vue";
import UserManagement from "../views/UserManagement/UserManagement.vue";
import UserManagementDetail from "../views/UserManagement/UserManagementDetail.vue";
import store from "../store";
import Forbidden from "../views/errors/Forbidden.vue";

/**
 * Check if user has allowed role (using Vuex store - secure)
 * @param {string[]} allowedRoles - Array of allowed roles
 * @returns {boolean} True if user has one of the allowed roles
 */
function checkRole(allowedRoles) {
  const userRole = store.getters['auth/userRole'];
  if (!userRole || !allowedRoles) return false;
  return allowedRoles.includes(userRole);
}

/**
 * Check if KYB status is approved (using Vuex store - secure)
 * @returns {boolean} True if statusKyb === 'approved'
 */
function isKybApproved() {
  return store.getters['auth/statusKyb'] === 'approved';
}

const routes = [
  {
    path: "/",
    name: "Home",
    redirect: "/home",
  },
  {
    path: "/403",
    name: "Forbidden",
    component: Forbidden,
  },
  {
    path: "/home",
    name: "DashboardApp",
    component: DashboardApp,
    meta: { requiresAuth: true, allowedRoles: ["Admin", "IT", "Finance", "Staff"] },
  },
  {
    path: "/company-detail",
    name: "DetailCompany",
    component: DetailCompany,
    meta: { requiresAuth: true, allowedRoles: ["Admin", "IT", "Finance", "Staff"] },
  },
  {
    path: "/company-document",
    name: "DocumentCompany",
    component: DocumentCompany,
    meta: { requiresAuth: true, allowedRoles: ["Admin", "IT", "Finance", "Staff"] },
  },
  {
    path: "/company-bank",
    name: "BankCompany",
    component: BankCompany,
    meta: { requiresAuth: true, allowedRoles: ["Admin", "IT", "Finance", "Staff"] },
  },
  {
    path: "/balance",
    name: "Balance",
    component: Balance,
    meta: { requiresAuth: true, allowedRoles: ["Admin", "Finance", "Staff"] },
  },
  {
    path: "/transaction",
    name: "Transaction",
    component: Transaction,
    meta: { requiresAuth: true, allowedRoles: ["Admin", "Finance", "Staff"] },
  },
  {
    path: "/transaction/:id",
    name: "TransactionDetail",
    component: TransactionDetail,
    meta: { requiresAuth: true, allowedRoles: ["Admin", "Finance", "Staff"] },
  },
  {
    path: "/signin",
    name: "Signin",
    component: Signin,
  },
  {
    path: "/verify-completed",
    name: "VerifyCompleted",
    component: VerifyCompleted,
    // beforeEnter: (to, from, next) => {
    //   const store = useStore();
    //   if (!store.state.auth.email) {
    //     next({ name: "Signup" });
    //   } else {
    //     next();
    //   }
    // },
  },
  {
    path: "/email-verify",
    name: "VerifyEmail",
    component: VerifyEmail,
    beforeEnter: (to, from, next) => {
      const store = useStore();
      if (!store.state.auth.email) {
        next({ name: "Signup" });
      } else {
        next();
      }
    },
  },
  {
    path: "/signup",
    name: "Signup",
    component: Signup,
  },
  {
    path: "/verify-otp",
    name: "VerifyOtp",
    component: Otp,
    beforeEnter: (to, from, next) => {
      const store = useStore();
      if (!store.state.auth.email) {
        next({ name: "Signin" });
      } else {
        next();
      }
    },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPassword,
  },
  {
    path: "/forgot-password-notify",
    name: "ForgotPasswordNotify",
    component: ForgotPasswordNotify,
    beforeEnter: (to, from, next) => {
      const store = useStore();
      if (!store.state.auth.email) {
        next({ name: "ForgotPasswordNotify" });
      } else {
        next();
      }
    },
  },
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
    meta: { requiresAuth: true, allowedRoles: ["Admin", "IT", "Finance", "Staff"] },
  },
  {
    path: "/reset-password/:resetPasswordToken",
    name: "ResetPassword",
    component: ResetPassword,
  },
  {
    path: "/reset-password-notify",
    name: "ResetPasswordNotify",
    component: ResetPasswordNotify,
  },
  {
    path: "/help-center",
    name: "HelpCenter",
    component: HelpCenter,
    meta: { requiresAuth: true, allowedRoles: ["Admin", "IT", "Finance", "Staff"] },
  },
  {
    path: "/transfer",
    name: "Transfer",
    component: Transfer,
    meta: { requiresAuth: true, allowedRoles: ["Finance", "Admin"], requiresKybApproved: true },
  },
  {
    path: "/single-transfer",
    name: "SingleTransfer",
    component: SingleTransfer,
    meta: { requiresAuth: true, allowedRoles: ["Finance", "Admin"], requiresKybApproved: true },
  },
  {
    path: "/upload-ppob",
    name: "UploadTopup",
    component: UploadTopup,
    meta: { requiresAuth: true, allowedRoles: ["Finance", "Admin"], requiresKybApproved: true },
  },
  {
    path: "/verify-otp-verification",
    name: "OtpVerification",
    component: OtpVerification,
    meta: { requiresAuth: true, allowedRoles: ["Admin", "IT", "Finance", "Staff"] },
  },
  {
    path: "/payment-instructions", // Or a more descriptive path like /transfer/:batchId/va-payment
    name: "VirtualAccountDisplay",
    component: VirtualAccountDisplay,
    meta: { requiresAuth: true, allowedRoles: ["Finance", "Admin"], requiresKybApproved: true },
  },
  {
    path: "/payment-success", // Define the path for the success page
    name: "PaymentSuccess", // Define the route name
    component: PaymentSuccess,
    meta: { requiresAuth: true, allowedRoles: ["Finance", "Admin"], requiresKybApproved: true }, // Assuming this page also requires authentication
  },
  {
    path: "/single-transfer/payment-success",
    name: "SingleTransferPaymentSuccess",
    component: SingleTransferPaymentSuccess,
    meta: { requiresAuth: true, allowedRoles: ["Finance", "Admin"], requiresKybApproved: true },
  },
  {
    path: "/payment-cancelled", // Path untuk halaman cancelled
    name: "PaymentCancelled", // Nama route
    component: PaymentCancelled,
    meta: { requiresAuth: true, allowedRoles: ["Finance", "Admin"], requiresKybApproved: true },
  },
  {
    path: "/ppob-payment-success",
    name: "PPOBPaymentSuccess",
    component: PPOBPaymentSuccess,
    meta: { requiresAuth: true, allowedRoles: ["Finance", "Admin"], requiresKybApproved: true },
  },
  {
    path: "/ppob-payment-instructions",
    name: "PPOBVirtualAccountDisplay",
    component: PPOBVirtualAccountDisplay,
    meta: { requiresAuth: true, allowedRoles: ["Finance", "Admin"], requiresKybApproved: true },
  },
  {
    path: "/integrations",
    name: "Integrations",
    component: Integrations,
    meta: { requiresAuth: true, allowedRoles: ["Admin", "IT"] },
  },
  {
    path: "/user-management",
    name: "UserManagement",
    component: UserManagement,
    meta: { requiresAuth: true, allowedRoles: ["Admin"] },
  },
  {
    path: "/user-management/:id",
    name: "UserManagementDetail",
    component: UserManagementDetail,
    meta: { requiresAuth: true, allowedRoles: ["Admin"] },
  },
  {
    path: "/set-password",
    name: "SetPassword",
    component: SetPassword,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active",
});

const publicPages = [
  "Signin",
  "SetPassword",
  "Signup",
  "VerifyOtp",
  "VerifyEmail",
  "VerifyCompleted",
  "ForgotPassword",
  "ForgotPasswordNotify",
  "ResetPassword",
  "ResetPasswordNotify",
];

router.beforeEach(async (to, from, next) => {
  const token = store.state.auth.token || localStorage.getItem("auth_token");

  if (!token && !publicPages.includes(to.name)) {
    next({ name: "Signin" });
  } else {
    if (token && !store.state.auth.token) {
      store.commit("auth/setToken", token);
    }

    // Reset session timeout on navigation if user is authenticated
    if (token && !publicPages.includes(to.name)) {
      sessionTimeout.resetTimer();
    }

    // Role-based access control: block direct URL access if role is not allowed
    // Fetch profile from API if role not in store (handles page refresh)
    if (token && to.meta?.allowedRoles) {
      // If role not in store, fetch from API first
      if (!store.getters['auth/userRole']) {
        await store.dispatch('auth/fetchUserProfile');
      }
      
      // Check role using store (secure - cannot be manipulated from frontend)
      if (!checkRole(to.meta.allowedRoles)) {
        next({ name: "Forbidden", query: { from: to.fullPath } });
        return;
      }

      // KYB status check for transfer-related routes
      if (to.meta?.requiresKybApproved && !isKybApproved()) {
        next({ name: "Forbidden", query: { from: to.fullPath, reason: 'kyb_not_approved' } });
        return;
      }
    }

    next();
  }
});

export default router;
